import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CHUNK_ERROR_RX, RELOAD_GUARD_KEY } from '@/shared/lib/lazyRetry';

/**
 * Fallback amigable para errores lanzados por rutas del router.
 * React Router v6.4+ lo llama automáticamente cuando algún lazy/loader
 * o el render de una ruta lanza.
 */
export function RouteErrorElement() {
  const { t } = useTranslation();
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : t('common.error_unknown');

  const isChunkError =
    error instanceof Error &&
    CHUNK_ERROR_RX.test(error.message);

  const reload = () => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  const hardReset = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.href = '/';
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: '#F7F9FF',
        color: '#0F172A',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 380 }}>
        <div
          style={{
            width: 56,
            height: 56,
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 14,
            background: '#002DA0',
            color: '#fff',
            fontSize: 28,
          }}
        >
          ÷
        </div>
        <h1
          style={{
            fontSize: 22,
            marginBottom: 8,
            fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
            color: '#002DA0',
          }}
        >
          {t('common.error_boundary_title')}
        </h1>
        <p style={{ fontSize: 14, color: '#64748B', marginBottom: 20 }}>
          {isChunkError
            ? t('common.error_boundary_chunk_body')
            : t('common.error_boundary_generic_body')}
        </p>
        <button
          type="button"
          onClick={reload}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: '#002DA0',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: 10,
          }}
        >
          {t('common.error_boundary_reload')}
        </button>
        <button
          type="button"
          onClick={hardReset}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'transparent',
            color: '#64748B',
            border: '1px solid #DDE2F5',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {t('common.error_boundary_hard_reset')}
        </button>
        <details style={{ marginTop: 20, textAlign: 'left' }}>
          <summary style={{ fontSize: 12, color: '#6B6B80', cursor: 'pointer' }}>
            {t('common.error_boundary_details_summary')}
          </summary>
          <pre
            style={{
              marginTop: 8,
              fontSize: 11,
              color: '#8B2C2C',
              background: '#FBEDED',
              padding: 8,
              borderRadius: 4,
              overflow: 'auto',
              maxHeight: 200,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {message}
          </pre>
        </details>
      </div>
    </div>
  );
}
